import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Gift, CheckCircle2, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { SlotPicker } from "../components/SlotPicker";

export default function RedeemGift() {
  const { code } = useParams();
  const [gift, setGift] = useState(null);
  const [state, setState] = useState("loading");
  const [f, setF] = useState({ name: "", email: "", topics: "", slot_start: "" });
  const [refresh, setRefresh] = useState(0);
  const [booked, setBooked] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gifts/${code}`)
      .then(({ data }) => {
        setGift(data);
        setF((x) => ({ ...x, name: data.recipient_name, email: data.recipient_email }));
        setState(data.redeemed ? "used" : "form");
      })
      .catch(() => setState("invalid"));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.slot_start) return toast.error("Please pick a time first.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/gifts/${code}/redeem`, f);
      setBooked(data.slot_label);
      setState("booked");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || "Could not book. Please try again.");
      setF((x) => ({ ...x, slot_start: "" }));
      setRefresh((r) => r + 1);
    }
    setSending(false);
  };

  const inp = "wwr-input w-full rounded-xl px-4 py-3 text-sm";
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="card-surface p-8 md:p-10 max-w-xl w-full" data-testid="redeem-page">
        {state === "loading" && <Loader2 className="mx-auto text-teal-400 animate-spin" size={32} />}
        {state === "invalid" && <p className="text-center text-neutral-400" data-testid="redeem-invalid">We couldn't find that gift code.</p>}
        {state === "used" && <p className="text-center text-neutral-400" data-testid="redeem-used">This gift has already been used to book a session.</p>}
        {state === "booked" && (
          <div className="text-center" data-testid="redeem-booked">
            <CheckCircle2 className="mx-auto text-teal-400 mb-4" size={44} />
            <h1 className="font-display text-3xl mb-2">You're booked.</h1>
            <p className="text-sm text-neutral-300">{booked}. Check your email for the details and an add-to-calendar link.</p>
          </div>
        )}
        {state === "form" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-xs uppercase tracking-[0.28em] text-amber-300/80 inline-flex items-center gap-2"><Gift size={13} /> A gift for you</div>
            <h1 className="font-display text-3xl text-neutral-100">{gift.from_name || "Someone"} wanted you to have this.</h1>
            {gift.message && <blockquote className="border-l-2 border-amber-400/60 pl-4 text-sm italic text-neutral-300" data-testid="redeem-message">"{gift.message}"</blockquote>}
            <p className="text-sm text-neutral-400">A free 30-minute Google Meet with Gabi. No pressure, no judgment. Pick a time that works for you.</p>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-label="Your name" placeholder="Your name" data-testid="redeem-name" className={inp} />
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} aria-label="Your email" placeholder="Your email" data-testid="redeem-email" className={inp} />
            <textarea rows={3} value={f.topics} onChange={(e) => setF({ ...f, topics: e.target.value })} aria-label="Topics" placeholder="Anything you'd like to talk about? (optional)" data-testid="redeem-topics" className={inp} />
            <SlotPicker value={f.slot_start} onChange={(v) => setF({ ...f, slot_start: v })} refreshKey={refresh} />
            <button type="submit" disabled={sending} data-testid="redeem-submit" className="btn-primary w-full py-3 text-sm disabled:opacity-60">
              {sending ? "Booking…" : "Book my session"}
            </button>
          </form>
        )}
        <Link to="/" className="block text-center mt-8 text-sm text-teal-300 hover:text-teal-200" data-testid="redeem-home">Visit Why We Recover</Link>
      </div>
    </div>
  );
}
